import { Injectable } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';

import { EditEventAction } from '@connect-services/client/models/edit-event.model';
import { ExtraObject, Layer } from '@connect-services/client/sdk/api';
import { EditState } from '@connect-services/client/services/edit-mode-state.service';

@Injectable({
    providedIn: 'root',
})
export class ExtraObjectService {
    public getExtraObjectsEditActions(
        extraObjects: ExtraObject[],
        extraObjectsLayers: Map<string, Layer>,
        projectState: EditState,
        depositId?: string,
    ): EditEventAction[] {
        return extraObjects.reduce((events: EditEventAction[], extraObject: ExtraObject): EditEventAction[] => {
            if (!extraObject.after && !extraObject.before) {
                return events;
            }

            const extraObjectProperties = (extraObject.after ?? extraObject.before)!;

            const layer = extraObjectsLayers.get(extraObject.table);
            const accessKey = layer?.id ?? extraObject.table;
            const existingObjectState = projectState[accessKey]?.[extraObjectProperties['uuid']];

            const businessIdentifier = layer?.businessIdentifier
                ? {
                      key: layer.businessIdentifier,
                      value: extraObjectProperties[layer.businessIdentifier],
                  }
                : {
                      key: 'uuid',
                      value: extraObjectProperties['uuid'],
                  };

            const entityId: string = extraObjectProperties['uuid'] ?? uuidv4();
            const extraProperties = {
                businessIdentifier: businessIdentifier.value,
                deposit_id:
                    depositId ?? extraObjectProperties['deposit_id'] ?? existingObjectState?.properties?.['deposit_id'],
                uuid: entityId,
            };

            return [
                ...events,
                {
                    action: extraObject.action,
                    after: {
                        ...extraObject.after,
                        ...(extraObject.after && extraProperties),
                    },
                    before: {
                        ...extraObject.before,
                        ...existingObjectState?.properties,
                        ...(extraObject.before && extraProperties),
                    },
                    businessIdentifier: businessIdentifier,
                    entityId: entityId,
                    layerId: layer?.id,
                    saveOrder: extraObject.saveOrder,
                    tableName: extraObject.table,
                },
            ];
        }, []);
    }

    public getExtraObjectsLayers(extraObjects: ExtraObject[], layers: Layer[], depositId?: string): Map<string, Layer> {
        return extraObjects.reduce((acc: Map<string, Layer>, extraObject: ExtraObject) => {
            const depositValue = extraObject.after?.['deposit_id'] ?? depositId;
            const extraObjectLayer = layers.find(
                (layer: Layer) => layer.deposit === depositValue && layer.tableName === extraObject.table,
            );

            if (!extraObjectLayer) {
                return acc;
            }

            return acc.set(extraObject.table, extraObjectLayer);
        }, new Map<string, Layer>());
    }
}
